import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import {
  captureCustomerOwnedSessionStorageMutationProof,
  discardCustomerOwnedSessionStorageMutationExactStrict,
  readCustomerOwnedSessionStorageRawValueStable,
} from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import { prepareRetiredGroupAppointmentWaitlistStorageForCurrentAccess } from "@/app/booking/group/groupAppointmentRetiredWaitlistStorage";
import {
  validateAppointmentWaitlistConfigurationPublication,
  validateAppointmentWaitlistPreferencesForConfiguration,
  validateRetiredGroupAppointmentWaitlistConfigurationPublicationForStorageMigration,
  validateRetiredGroupAppointmentWaitlistPreferencesForConfigurationForStorageMigration,
  type AppointmentWaitlistPreferences,
} from "./appointmentWaitlistConfiguration";
import {
  validateAppointmentWaitlistDraft,
  validateRetiredGroupAppointmentWaitlistDraftForStorageMigration,
} from "./appointmentWaitlistDraft";
import {
  validateAppointmentWaitlistReceipt,
  validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration,
} from "./appointmentWaitlistReceipt";
import {
  validateAppointmentWaitlistSubmissionAttempt,
  validateRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration,
} from "./appointmentWaitlistSubmissionAttempt";
import {
  validateIndividualAppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigration,
  validateRetiredGroupAppointmentWaitlistTerminalAttemptAggregateForStorageMigration,
} from "./appointmentWaitlistTerminalAttemptAggregate";
import {
  validateIndividualSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration,
  validateRetiredGroupSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration,
} from "@/app/specialists/locationGateway/terminalOriginStore";
import {
  APPOINTMENT_WAITLIST_DRAFT_STORAGE_MAX_SERIALIZED_CHARACTERS,
  APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_MAX_SERIALIZED_CHARACTERS,
  APPOINTMENT_WAITLIST_RECEIPT_STORAGE_MAX_SERIALIZED_CHARACTERS,
  APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_MAX_SERIALIZED_CHARACTERS,
  APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_MAX_SERIALIZED_CHARACTERS,
  SPECIALIST_LOCATION_GATEWAY_WAITLIST_TERMINAL_ORIGIN_STORAGE_MAX_SERIALIZED_CHARACTERS,
} from "./appointmentWaitlistStorageLimits";

const SHARED_WAITLIST_STORAGE = Object.freeze({
  drafts: {
    key: "volera:appointment-waitlist-drafts:v3",
    storeKind: "appointment-waitlist-drafts",
    payloadKey: "draft",
    maxSerializedCharacters:
      APPOINTMENT_WAITLIST_DRAFT_STORAGE_MAX_SERIALIZED_CHARACTERS,
    requiredModePaths: [
      ["draft", "appointmentDraft", "mode"],
      ["draft", "configurationPublication", "plan", "mode"],
    ],
    optionalModePaths: [],
    lineageIdPaths: [
      ["draft", "draftId"],
      ["draft", "configurationPublication", "waitlistDraftId"],
    ],
    sourceLineageIdPathGroups: [[
      ["draft", "appointmentDraft", "draftId"],
      ["draft", "configurationPublication", "sourceAppointmentDraftId"],
      ["draft", "configurationPublication", "plan", "draftId"],
    ]],
  },
  receipts: {
    key: "volera:appointment-waitlist-receipts:v3",
    storeKind: "appointment-waitlist-receipts",
    payloadKey: "draft",
    maxSerializedCharacters:
      APPOINTMENT_WAITLIST_RECEIPT_STORAGE_MAX_SERIALIZED_CHARACTERS,
    requiredModePaths: [
      ["draft", "waitlistDraft", "appointmentDraft", "mode"],
      [
        "draft",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "mode",
      ],
    ],
    optionalModePaths: [],
    lineageIdPaths: [
      ["draft", "draftId"],
      ["draft", "waitlistDraft", "draftId"],
      [
        "draft",
        "waitlistDraft",
        "configurationPublication",
        "waitlistDraftId",
      ],
    ],
    sourceLineageIdPathGroups: [[
      ["draft", "waitlistDraft", "appointmentDraft", "draftId"],
      [
        "draft",
        "waitlistDraft",
        "configurationPublication",
        "sourceAppointmentDraftId",
      ],
      [
        "draft",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "draftId",
      ],
    ]],
  },
  attempts: {
    key: "volera:appointment-waitlist-submission-attempts:v1",
    storeKind: "appointment-waitlist-submission-attempts",
    payloadKey: "attempt",
    maxSerializedCharacters:
      APPOINTMENT_WAITLIST_SUBMISSION_ATTEMPT_STORAGE_MAX_SERIALIZED_CHARACTERS,
    requiredModePaths: [
      ["attempt", "sourceAppointmentDraft", "mode"],
      ["attempt", "waitlistDraft", "appointmentDraft", "mode"],
      [
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "mode",
      ],
    ],
    optionalModePaths: [],
    lineageIdPaths: [
      ["attempt", "draftId"],
      ["attempt", "waitlistDraft", "draftId"],
      [
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "waitlistDraftId",
      ],
    ],
    sourceLineageIdPathGroups: [[
      ["attempt", "sourceAppointmentDraft", "draftId"],
      ["attempt", "waitlistDraft", "appointmentDraft", "draftId"],
      [
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "sourceAppointmentDraftId",
      ],
      [
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "draftId",
      ],
    ]],
  },
  terminalAggregates: {
    key: "volera:appointment-waitlist-terminal-attempt-aggregates:v1",
    storeKind: "appointment-waitlist-terminal-attempt-aggregates",
    payloadKey: "draft",
    maxSerializedCharacters:
      APPOINTMENT_WAITLIST_TERMINAL_ATTEMPT_AGGREGATE_STORAGE_MAX_SERIALIZED_CHARACTERS,
    requiredModePaths: [
      ["draft", "attempt", "sourceAppointmentDraft", "mode"],
      [
        "draft",
        "attempt",
        "waitlistDraft",
        "appointmentDraft",
        "mode",
      ],
      [
        "draft",
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "mode",
      ],
    ],
    optionalModePaths: [
      {
        ownerPath: ["draft", "receipt"],
        modePaths: [
          [
            "draft",
            "receipt",
            "waitlistDraft",
            "appointmentDraft",
            "mode",
          ],
          [
            "draft",
            "receipt",
            "waitlistDraft",
            "configurationPublication",
            "plan",
            "mode",
          ],
        ],
        lineageIdPaths: [
          ["draft", "receipt", "draftId"],
          ["draft", "receipt", "waitlistDraft", "draftId"],
          [
            "draft",
            "receipt",
            "waitlistDraft",
            "configurationPublication",
            "waitlistDraftId",
          ],
        ],
        sourceLineageIdPathGroups: [[
          [
            "draft",
            "receipt",
            "waitlistDraft",
            "appointmentDraft",
            "draftId",
          ],
          [
            "draft",
            "receipt",
            "waitlistDraft",
            "configurationPublication",
            "sourceAppointmentDraftId",
          ],
          [
            "draft",
            "receipt",
            "waitlistDraft",
            "configurationPublication",
            "plan",
            "draftId",
          ],
        ]],
      },
    ],
    lineageIdPaths: [
      ["draft", "draftId"],
      ["draft", "attempt", "draftId"],
      ["draft", "attempt", "waitlistDraft", "draftId"],
      [
        "draft",
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "waitlistDraftId",
      ],
    ],
    sourceLineageIdPathGroups: [[
      ["draft", "attempt", "sourceAppointmentDraft", "draftId"],
      [
        "draft",
        "attempt",
        "waitlistDraft",
        "appointmentDraft",
        "draftId",
      ],
      [
        "draft",
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "sourceAppointmentDraftId",
      ],
      [
        "draft",
        "attempt",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "draftId",
      ],
    ]],
  },
  specialistTerminalOrigins: {
    key: "volera:specialist-location-gateway-terminal-origins:appointment-waitlist:v1",
    storeKind: "specialist-location-gateway-appointment-waitlist-origins",
    payloadKey: "draft",
    maxSerializedCharacters:
      SPECIALIST_LOCATION_GATEWAY_WAITLIST_TERMINAL_ORIGIN_STORAGE_MAX_SERIALIZED_CHARACTERS,
    requiredModePaths: [
      [
        "draft",
        "receipt",
        "waitlistDraft",
        "appointmentDraft",
        "mode",
      ],
      [
        "draft",
        "receipt",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "mode",
      ],
    ],
    optionalModePaths: [],
    lineageIdPaths: [
      ["draft", "draftId"],
      ["draft", "receipt", "draftId"],
      ["draft", "receipt", "waitlistDraft", "draftId"],
      [
        "draft",
        "receipt",
        "waitlistDraft",
        "configurationPublication",
        "waitlistDraftId",
      ],
    ],
    sourceLineageIdPathGroups: [[
      [
        "draft",
        "receipt",
        "waitlistDraft",
        "appointmentDraft",
        "draftId",
      ],
      [
        "draft",
        "receipt",
        "waitlistDraft",
        "configurationPublication",
        "sourceAppointmentDraftId",
      ],
      [
        "draft",
        "receipt",
        "waitlistDraft",
        "configurationPublication",
        "plan",
        "draftId",
      ],
    ]],
  },
  pendingTransitions: {
    key: "volera:appointment-waitlist-pending-transition:v1",
  },
} as const);

type RecordValue = Record<string, unknown>;
type AppointmentMode = "individual" | "group";

interface ArrayEnvelopeSnapshot {
  readonly storageKey: string;
  readonly rawValue: string;
  readonly parsed: RecordValue;
  readonly entries: readonly unknown[];
  readonly groupEntryIndexes: ReadonlySet<number>;
  readonly groupDraftIds: ReadonlySet<string>;
  readonly modesByDraftId: ReadonlyMap<string, AppointmentMode>;
}

interface PendingEnvelopeSnapshot {
  readonly storageKey: string;
  readonly rawValue: string;
  readonly parsed: RecordValue;
  readonly entries: RecordValue;
  readonly groupDraftIds: ReadonlySet<string>;
  readonly modesByDraftId: ReadonlyMap<string, AppointmentMode>;
}

interface OptionalModePath {
  readonly ownerPath: readonly string[];
  readonly modePaths: readonly (readonly string[])[];
  readonly lineageIdPaths: readonly (readonly string[])[];
  readonly sourceLineageIdPathGroups: readonly (readonly (readonly string[])[])[];
}

interface ArrayEnvelopeSpec {
  readonly key: string;
  readonly storeKind: string;
  readonly payloadKey: "draft" | "attempt";
  readonly maxSerializedCharacters: number;
  readonly requiredModePaths: readonly (readonly string[])[];
  readonly optionalModePaths: readonly OptionalModePath[];
  readonly lineageIdPaths: readonly (readonly string[])[];
  readonly sourceLineageIdPathGroups: readonly (readonly (readonly string[])[])[];
}

interface MigrationState {
  reentrantAccessObserved: boolean;
}

const activeMigrations = new WeakMap<Storage, MigrationState>();

function isRecord(value: unknown): value is RecordValue {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: RecordValue, expected: readonly string[]) {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

function isStoredEpoch(value: unknown, evaluatedAtEpochMs: number): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= evaluatedAtEpochMs + 5 * 60_000
  );
}

function readSafeMigrationEpoch(evaluatedAtEpochMs?: number) {
  try {
    const candidate = evaluatedAtEpochMs ?? Date.now();
    return Number.isSafeInteger(candidate) &&
      candidate >= 0 &&
      candidate <= 8_640_000_000_000_000
      ? candidate
      : null;
  } catch {
    return null;
  }
}

function nested(value: unknown, path: readonly string[]) {
  let current: unknown = value;
  for (const part of path) {
    if (!isRecord(current)) return undefined;
    current = current[part];
  }
  return current;
}

function haveExactSerializableStructure(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function canonicalEntryPayloadForMode(input: {
  entry: RecordValue;
  spec: ArrayEnvelopeSpec;
  mode: AppointmentMode;
  evaluatedAtEpochMs: number;
}) {
  const payload = input.entry[input.spec.payloadKey];
  switch (input.spec.key) {
    case SHARED_WAITLIST_STORAGE.drafts.key: {
      const validation =
        input.mode === "individual"
          ? validateAppointmentWaitlistDraft(payload)
          : validateRetiredGroupAppointmentWaitlistDraftForStorageMigration(
              payload,
            );
      return validation.valid ? validation.draft : null;
    }
    case SHARED_WAITLIST_STORAGE.receipts.key: {
      const validation =
        input.mode === "individual"
          ? validateAppointmentWaitlistReceipt(payload)
          : validateRetiredGroupAppointmentWaitlistReceiptForStorageMigration(
              payload,
            );
      return validation.valid ? validation.receipt : null;
    }
    case SHARED_WAITLIST_STORAGE.attempts.key: {
      const validation =
        input.mode === "individual"
          ? validateAppointmentWaitlistSubmissionAttempt(payload, {
              evaluatedAtEpochMs: input.evaluatedAtEpochMs,
            })
          : validateRetiredGroupAppointmentWaitlistSubmissionAttemptForStorageMigration(
              payload,
              { evaluatedAtEpochMs: input.evaluatedAtEpochMs },
            );
      return validation.valid ? validation.attempt : null;
    }
    case SHARED_WAITLIST_STORAGE.terminalAggregates.key: {
      const validation =
        input.mode === "individual"
          ? validateIndividualAppointmentWaitlistTerminalAttemptAggregateV1ForStorageMigration(
              payload,
            )
          : validateRetiredGroupAppointmentWaitlistTerminalAttemptAggregateForStorageMigration(
              payload,
            );
      return validation.valid ? validation.aggregate : null;
    }
    case SHARED_WAITLIST_STORAGE.specialistTerminalOrigins.key: {
      const validation =
        input.mode === "individual"
          ? validateIndividualSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration(
              payload,
            )
          : validateRetiredGroupSpecialistLocationGatewayWaitlistTerminalOriginForStorageMigration(
              payload,
            );
      return validation.valid ? validation.draft : null;
    }
  }
  return null;
}

function isAppointmentMode(value: unknown): value is AppointmentMode {
  return value === "individual" || value === "group";
}

function inspectEntryModeAndLineage(
  entry: RecordValue,
  spec: ArrayEnvelopeSpec,
  evaluatedAtEpochMs: number,
): AppointmentMode | null {
  const draftId = entry.draftId;
  if (
    !hasExactKeys(entry, ["draftId", "savedAtEpochMs", spec.payloadKey]) ||
    !isIdentifier(draftId) ||
    !isStoredEpoch(entry.savedAtEpochMs, evaluatedAtEpochMs)
  ) {
    return null;
  }

  const modes: AppointmentMode[] = [];
  for (const path of spec.requiredModePaths) {
    const mode = nested(entry, path);
    if (!isAppointmentMode(mode)) return null;
    modes.push(mode);
  }

  const lineageIdPaths = [...spec.lineageIdPaths];
  const sourceLineageIdPathGroups = [...spec.sourceLineageIdPathGroups];
  for (const optional of spec.optionalModePaths) {
    const owner = nested(entry, optional.ownerPath);
    if (owner === null || owner === undefined) continue;
    if (!isRecord(owner)) return null;
    for (const path of optional.modePaths) {
      const mode = nested(entry, path);
      if (!isAppointmentMode(mode)) return null;
      modes.push(mode);
    }
    lineageIdPaths.push(...optional.lineageIdPaths);
    sourceLineageIdPathGroups.push(
      ...optional.sourceLineageIdPathGroups,
    );
  }

  if (modes.length === 0 || modes.some((mode) => mode !== modes[0])) {
    return null;
  }
  if (lineageIdPaths.some((path) => nested(entry, path) !== draftId)) {
    return null;
  }
  for (const pathGroup of sourceLineageIdPathGroups) {
    const lineageIds = pathGroup.map((path) => nested(entry, path));
    if (
      lineageIds.length < 2 ||
      lineageIds.some((lineageId) => !isIdentifier(lineageId)) ||
      lineageIds.some((lineageId) => lineageId !== lineageIds[0])
    ) {
      return null;
    }
  }
  const mode = modes[0];
  const canonicalPayload = canonicalEntryPayloadForMode({
    entry,
    spec,
    mode,
    evaluatedAtEpochMs,
  });
  return canonicalPayload &&
    haveExactSerializableStructure(canonicalPayload, entry[spec.payloadKey])
    ? mode
    : null;
}

function parseArrayEnvelope(input: {
  spec: ArrayEnvelopeSpec;
  rawValue: string;
  evaluatedAtEpochMs: number;
}): ArrayEnvelopeSnapshot | null {
  try {
    if (input.rawValue.length > input.spec.maxSerializedCharacters) {
      return null;
    }
    const parsed = JSON.parse(input.rawValue) as unknown;
    if (
      !isRecord(parsed) ||
      !hasExactKeys(parsed, ["storeVersion", "storeKind", "entries"]) ||
      parsed.storeVersion !== 1 ||
      parsed.storeKind !== input.spec.storeKind ||
      !Array.isArray(parsed.entries) ||
      parsed.entries.length > 8
    ) {
      return null;
    }

    const seenDraftIds = new Set<string>();
    const groupEntryIndexes = new Set<number>();
    const groupDraftIds = new Set<string>();
    const modesByDraftId = new Map<string, AppointmentMode>();
    for (const [index, entry] of parsed.entries.entries()) {
      if (!isRecord(entry) || !isIdentifier(entry.draftId)) return null;
      if (seenDraftIds.has(entry.draftId)) return null;
      const mode = inspectEntryModeAndLineage(
        entry,
        input.spec,
        input.evaluatedAtEpochMs,
      );
      if (!mode) return null;
      seenDraftIds.add(entry.draftId);
      modesByDraftId.set(entry.draftId, mode);
      if (mode === "group") {
        groupEntryIndexes.add(index);
        groupDraftIds.add(entry.draftId);
      }
    }

    return {
      storageKey: input.spec.key,
      rawValue: input.rawValue,
      parsed,
      entries: parsed.entries,
      groupEntryIndexes,
      groupDraftIds,
      modesByDraftId,
    };
  } catch {
    return null;
  }
}

function parsePendingEnvelope(
  rawValue: string,
  evaluatedAtEpochMs: number,
): PendingEnvelopeSnapshot | null {
  try {
    if (
      rawValue.length >
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_MAX_SERIALIZED_CHARACTERS
    ) {
      return null;
    }
    const parsed = JSON.parse(rawValue) as unknown;
    if (
      !isRecord(parsed) ||
      !hasExactKeys(parsed, ["schemaVersion", "entries"]) ||
      parsed.schemaVersion !== 1 ||
      !isRecord(parsed.entries) ||
      Object.keys(parsed.entries).length > 12
    ) {
      return null;
    }
    const groupDraftIds = new Set<string>();
    const modesByDraftId = new Map<string, AppointmentMode>();
    for (const [draftId, pending] of Object.entries(parsed.entries)) {
      const configuration = isRecord(pending)
        ? pending.configurationPublication
        : null;
      const plan = isRecord(configuration) ? configuration.plan : null;
      const mode = isRecord(plan) ? plan.mode : null;
      if (
        !isIdentifier(draftId) ||
        !isRecord(pending) ||
        !hasExactKeys(pending, [
          "schemaVersion",
          "kind",
          "waitlistDraftId",
          "sourceAppointmentDraftId",
          "expectedCustomerAccountId",
          "preferences",
          "configurationPublication",
          "createdAtEpochMs",
        ]) ||
        pending.schemaVersion !== 1 ||
        pending.kind !== "continue-after-auth" ||
        pending.waitlistDraftId !== draftId ||
        !isIdentifier(pending.sourceAppointmentDraftId) ||
        (pending.expectedCustomerAccountId !== null &&
          !isIdentifier(pending.expectedCustomerAccountId)) ||
        !isRecord(pending.preferences) ||
        !isRecord(configuration) ||
        configuration.waitlistDraftId !== draftId ||
        configuration.sourceAppointmentDraftId !==
          pending.sourceAppointmentDraftId ||
        !isRecord(plan) ||
        plan.draftId !== pending.sourceAppointmentDraftId ||
        !isAppointmentMode(mode) ||
        !isStoredEpoch(pending.createdAtEpochMs, evaluatedAtEpochMs)
      ) {
        return null;
      }
      const canonicalConfiguration =
        mode === "individual"
          ? validateAppointmentWaitlistConfigurationPublication(configuration)
          : validateRetiredGroupAppointmentWaitlistConfigurationPublicationForStorageMigration(
              configuration,
            );
      const canonicalPreferences = canonicalConfiguration
        ? mode === "individual"
          ? validateAppointmentWaitlistPreferencesForConfiguration({
              publication: canonicalConfiguration,
              preferences:
                pending.preferences as unknown as AppointmentWaitlistPreferences,
            })
          : validateRetiredGroupAppointmentWaitlistPreferencesForConfigurationForStorageMigration(
              {
                publication: canonicalConfiguration,
                preferences:
                  pending.preferences as unknown as AppointmentWaitlistPreferences,
              },
            )
        : null;
      if (
        !canonicalConfiguration ||
        !canonicalPreferences ||
        !haveExactSerializableStructure(
          canonicalConfiguration,
          configuration,
        ) ||
        !haveExactSerializableStructure(
          canonicalPreferences,
          pending.preferences,
        )
      ) {
        return null;
      }
      modesByDraftId.set(draftId, mode);
      if (mode === "group") groupDraftIds.add(draftId);
    }
    return {
      storageKey: SHARED_WAITLIST_STORAGE.pendingTransitions.key,
      rawValue,
      parsed,
      entries: parsed.entries,
      groupDraftIds,
      modesByDraftId,
    };
  } catch {
    return null;
  }
}

function serializeWithEntries(
  parsed: RecordValue,
  entries: readonly unknown[] | RecordValue,
) {
  const empty = Array.isArray(entries)
    ? entries.length === 0
    : Object.keys(entries).length === 0;
  return empty ? null : JSON.stringify({ ...parsed, entries });
}

function commitExactMigration(input: {
  storage: Storage;
  storageKey: string;
  expectedRawValue: string;
  nextRawValue: string | null;
}) {
  const mutationProof = captureCustomerOwnedSessionStorageMutationProof();
  return (
    discardCustomerOwnedSessionStorageMutationExactStrict({
      storage: input.storage,
      storageKey: input.storageKey,
      expectedRawValue: input.expectedRawValue,
      nextRawValue: input.nextRawValue,
      mutationIsCurrent: () => true,
      mutationProof,
    }).status === "committed"
  );
}

function failMigration() {
  quarantineCustomerOwnedSessionStorage();
  return false;
}

/**
 * One-way migration for Group rows once written into the ordinary V3 stores.
 * A row is removable only when every embedded appointment mode and Waitlist
 * draft identifier agrees on the same Group lineage. All six envelopes are
 * read and structurally proved before the first write, so malformed or
 * contradictory siblings leave the complete frontier untouched and fail
 * closed behind the customer-owned storage quarantine.
 */
export function migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage(
  evaluatedAtEpochMs?: number,
) {
  if (typeof window === "undefined") return true;

  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return failMigration();
  }

  const active = activeMigrations.get(storage);
  if (active) {
    active.reentrantAccessObserved = true;
    return failMigration();
  }
  const migrationState: MigrationState = {
    reentrantAccessObserved: false,
  };
  activeMigrations.set(storage, migrationState);

  try {
    if (!prepareRetiredGroupAppointmentWaitlistStorageForCurrentAccess()) {
      return false;
    }
    const migrationEpochMs = readSafeMigrationEpoch(evaluatedAtEpochMs);
    if (migrationEpochMs === null) return failMigration();
    const orderedArraySpecs: readonly ArrayEnvelopeSpec[] = [
      SHARED_WAITLIST_STORAGE.specialistTerminalOrigins,
      SHARED_WAITLIST_STORAGE.terminalAggregates,
      SHARED_WAITLIST_STORAGE.receipts,
      SHARED_WAITLIST_STORAGE.attempts,
      SHARED_WAITLIST_STORAGE.drafts,
    ];
    const arraySnapshots: ArrayEnvelopeSnapshot[] = [];
    for (const spec of orderedArraySpecs) {
      const stable = readCustomerOwnedSessionStorageRawValueStable({
        storage,
        storageKey: spec.key,
      });
      if (stable.status !== "ready") return failMigration();
      if (stable.rawValue === null) continue;
      const parsed = parseArrayEnvelope({
        spec,
        rawValue: stable.rawValue,
        evaluatedAtEpochMs: migrationEpochMs,
      });
      if (!parsed) return failMigration();
      arraySnapshots.push(parsed);
    }

    const pendingStable = readCustomerOwnedSessionStorageRawValueStable({
      storage,
      storageKey: SHARED_WAITLIST_STORAGE.pendingTransitions.key,
    });
    if (pendingStable.status !== "ready") return failMigration();
    const pendingSnapshot =
      pendingStable.rawValue === null
        ? null
        : parsePendingEnvelope(pendingStable.rawValue, migrationEpochMs);
    if (pendingStable.rawValue !== null && !pendingSnapshot) {
      return failMigration();
    }
    if (migrationState.reentrantAccessObserved) return failMigration();

    const retiredDraftIds = new Set<string>();
    const modesByDraftId = new Map<string, AppointmentMode>();
    for (const snapshot of arraySnapshots) {
      for (const [draftId, mode] of snapshot.modesByDraftId) {
        const existing = modesByDraftId.get(draftId);
        if (existing && existing !== mode) return failMigration();
        modesByDraftId.set(draftId, mode);
      }
      for (const draftId of snapshot.groupDraftIds) {
        retiredDraftIds.add(draftId);
      }
    }
    if (pendingSnapshot) {
      for (const [draftId, mode] of pendingSnapshot.modesByDraftId) {
        const existing = modesByDraftId.get(draftId);
        if (existing && existing !== mode) return failMigration();
        modesByDraftId.set(draftId, mode);
      }
      for (const draftId of pendingSnapshot.groupDraftIds) {
        retiredDraftIds.add(draftId);
      }
    }

    const draftSnapshot = arraySnapshots.find(
      ({ storageKey }) => storageKey === SHARED_WAITLIST_STORAGE.drafts.key,
    );
    for (const snapshot of arraySnapshots) {
      if (snapshot === draftSnapshot || snapshot.groupEntryIndexes.size === 0) {
        continue;
      }
      if (migrationState.reentrantAccessObserved) return failMigration();
      const retained = snapshot.entries.filter(
        (_entry, index) => !snapshot.groupEntryIndexes.has(index),
      );
      if (
        !commitExactMigration({
          storage,
          storageKey: snapshot.storageKey,
          expectedRawValue: snapshot.rawValue,
          nextRawValue: serializeWithEntries(snapshot.parsed, retained),
        })
      ) {
        return failMigration();
      }
    }

    if (pendingSnapshot && retiredDraftIds.size > 0) {
      if (migrationState.reentrantAccessObserved) return failMigration();
      const retained = Object.fromEntries(
        Object.entries(pendingSnapshot.entries).filter(
          ([key, value]) =>
            !retiredDraftIds.has(key) &&
            !(
              isRecord(value) &&
              typeof value.waitlistDraftId === "string" &&
              retiredDraftIds.has(value.waitlistDraftId)
            ),
        ),
      );
      if (
        Object.keys(retained).length !==
          Object.keys(pendingSnapshot.entries).length &&
        !commitExactMigration({
          storage,
          storageKey: pendingSnapshot.storageKey,
          expectedRawValue: pendingSnapshot.rawValue,
          nextRawValue: serializeWithEntries(pendingSnapshot.parsed, retained),
        })
      ) {
        return failMigration();
      }
    }

    if (draftSnapshot && draftSnapshot.groupEntryIndexes.size > 0) {
      if (migrationState.reentrantAccessObserved) return failMigration();
      const retained = draftSnapshot.entries.filter(
        (_entry, index) => !draftSnapshot.groupEntryIndexes.has(index),
      );
      if (
        !commitExactMigration({
          storage,
          storageKey: draftSnapshot.storageKey,
          expectedRawValue: draftSnapshot.rawValue,
          nextRawValue: serializeWithEntries(draftSnapshot.parsed, retained),
        })
      ) {
        return failMigration();
      }
    }

    return !migrationState.reentrantAccessObserved || failMigration();
  } catch {
    return failMigration();
  } finally {
    if (activeMigrations.get(storage) === migrationState) {
      activeMigrations.delete(storage);
    }
  }
}
